import { COUNTRY_ECOTAX_TYPE, CUSTOMER_LEVEL } from '@wyz/types';
import { Alert, Button, Card, Flex, Text, Title } from '@wyz/ui';
import clsx from 'clsx';
import { isNil } from 'lodash-es';
import * as React from 'react';
import { PrivateComponent } from '~/common/components';
import { useLocale, useI18n, Trans } from '~/common/context';
import { ROLES } from '~/common/helpers';
import { IUserCustomer } from '~/common/types';
import { ICart, ICartMeta } from '~/purchase/types';

type Props = {
  values: Pick<
    ICart,
    | 'taxesAmount'
    | 'totalExclTax'
    | 'totalExclTaxDelivery'
    | 'totalExclTaxEcoTax'
    | 'totalExclTaxProduct'
    | 'inclTaxTotal'
    | 'referencesCount'
    | 'articlesCount'
    | 'servicesCount'
  >;
  isSubmitting?: boolean;
  mode: 'ORDER' | 'CART';
  valuesChanged?: boolean;
  privileges: ICartMeta;
  customer: IUserCustomer;
  currency: string;
  canSubmitOrder?: boolean;
};
const TotalSection: React.FC<Props> = ({
  values,
  isSubmitting,
  privileges,
  mode,
  valuesChanged,
  currency,
  customer,
  canSubmitOrder = true,
}) => {
  const { t } = useI18n();
  const locale = useLocale();

  return (
    <Card className='p-4'>
      <Title heading='lg' weight='bold' className='mb-3' wrap>
        {mode === 'CART'
          ? t('app.purchase.cart_page.recap.title')
          : t('app.purchase.cart_page.order_recap.title')}
      </Title>
      <Flex justifyContent='between' alignItems='center' className='mb-3'>
        <Text heading='sm' weight='semi-bold'>
          <Trans i18nKey='app.purchase.cart_page.recap.references' />
        </Text>
        <Text heading='sm' weight='semi-bold' id={`reference-count`}>
          {values.referencesCount === 0 ? '-' : values.referencesCount}
        </Text>
      </Flex>
      <Flex
        justifyContent='between'
        alignItems='center'
        className={clsx({
          'mb-3': !isNil(values.servicesCount),
        })}
      >
        <Text heading='sm' weight='semi-bold'>
          <Trans i18nKey='app.purchase.cart_page.recap.articles' />
        </Text>
        <Text heading='sm' weight='semi-bold' id={`tyre-count`}>
          {values.articlesCount === 0 ? '-' : values.articlesCount}
        </Text>
      </Flex>
      <Flex justifyContent='between' alignItems='center'>
        <Text heading='sm' weight='semi-bold'>
          <Trans i18nKey='app.purchase.cart_page.recap.services' />
        </Text>
        <Text heading='sm' weight='semi-bold' id={`service-count`}>
          {values.servicesCount === 0 ? '-' : values.servicesCount}
        </Text>
      </Flex>
      <hr />
      <Flex justifyContent='between' alignItems='center' className='mb-3'>
        <Text heading='sm' weight='semi-bold' color='secondary-gray'>
          <Trans i18nKey='app.purchase.cart_page.product_excl_tax' />
        </Text>
        <Text
          heading='sm'
          weight='semi-bold'
          color='secondary-gray'
          id={`total-excl-tax-product`}
        >
          {locale.formatters.money(values.totalExclTaxProduct, {
            currency: currency,
          })}
        </Text>
      </Flex>
      {privileges.ecotaxType !== COUNTRY_ECOTAX_TYPE.NOT_DISPLAYED && (
        <Flex justifyContent='between' alignItems='center' className='mb-3'>
          <Text heading='sm' weight='semi-bold' color='secondary-gray'>
            <Trans i18nKey='app.purchase.cart_page.ecotax_excl_tax' />
          </Text>
          <Text
            heading='sm'
            weight='semi-bold'
            color='secondary-gray'
            id={`total-excl-tax-eco-tax`}
          >
            {privileges.ecotaxType === COUNTRY_ECOTAX_TYPE.INCLUDED_IN_TYRE ? (
              <Text heading='sm' weight='semi-bold' color='success'>
                <Trans i18nKey='app.purchase.cart_page.ecotax_included' />
              </Text>
            ) : (
              locale.formatters.money(values.totalExclTaxEcoTax ?? 0, {
                currency: currency,
              })
            )}
          </Text>
        </Flex>
      )}
      <Flex justifyContent='between' alignItems='center'>
        <Text heading='sm' weight='semi-bold' color='secondary-gray'>
          <Trans i18nKey='app.purchase.cart_page.delivery_excl_tax' />
        </Text>
        <Text
          heading='sm'
          weight='semi-bold'
          color='secondary-gray'
          id={`total-excl-tax-delivery`}
        >
          {locale.formatters.money(values.totalExclTaxDelivery, {
            currency: currency,
          })}
        </Text>
      </Flex>
      <hr />
      <Flex justifyContent='between' alignItems='center' className='mb-3'>
        <Text heading='normal' weight='bold'>
          <Trans i18nKey='app.common.total_price_without_tax' />
        </Text>
        <Title heading='lg' weight='bold' id={`total-excl-tax`}>
          {locale.formatters.money(values.totalExclTax, {
            currency: currency,
          })}
        </Title>
      </Flex>
      <Flex justifyContent='between' alignItems='center' className='mb-1'>
        <Text heading='sm' weight='semi-bold' color='secondary-gray'>
          <Trans i18nKey='app.common.vat' />
        </Text>
        <Text
          heading='sm'
          weight='semi-bold'
          color='secondary-gray'
          id={`taxes-amount`}
        >
          {locale.formatters.money(values.taxesAmount, {
            currency: currency,
          })}
        </Text>
      </Flex>
      <Flex justifyContent='between' alignItems='center' className='mb-2'>
        <Text heading='sm' weight='semi-bold' color='secondary-gray'>
          <Trans i18nKey='app.common.total_price_with_tax' />
        </Text>
        <Text
          heading='sm'
          weight='semi-bold'
          color='secondary-gray'
          id={`incl-tax-total`}
        >
          {locale.formatters.money(values.inclTaxTotal, {
            currency: currency,
          })}
        </Text>
      </Flex>
      {valuesChanged && (
        <Alert variant='warning'>
          <Trans i18nKey='app.purchase.cart.errors.changes_warning' />
        </Alert>
      )}
      {mode === 'CART' && (
        <PrivateComponent
          roles={
            customer?.level === CUSTOMER_LEVEL.N1
              ? [ROLES.ROLE_SUBMIT_CART]
              : [ROLES.ROLE_SUBMIT_REDISTRIBUTION_CART]
          }
          strategy='affirmative'
        >
          <Button
            id='submit-cart'
            type='submit'
            isLoading={isSubmitting}
            disabled={canSubmitOrder === false}
            variant='primary'
          >
            <Trans i18nKey='app.purchase.cart_page.table.order' />
          </Button>
        </PrivateComponent>
      )}
    </Card>
  );
};

export default TotalSection;
